import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { restPostDeviceCommand } from '../../../services/deviceCommands';
import { ILiveViewCommandParams, restGetActiveLiveViews } from '../service';
import { ILiveCameras } from '../types';
import { TStatus } from '../../../state/types';
import { TRootState } from '../../../store';

interface IState {
    liveCameras: ILiveCameras | null;
    status: TStatus;
}

export const initialState: IState = {
    liveCameras: null,
    status: 'idle',
};

export const startLiveView = createAsyncThunk(
    'post:ui/liveView',
    async (monitoredId: string, { rejectWithValue }) => {
        try {
            const response =
                await restPostDeviceCommand<ILiveViewCommandParams>(
                    { '@type': 'LIVE_VIEW', parameters: { state: 'start' } },
                    monitoredId
                );
            return response;
        } catch (e: any) {
            throw rejectWithValue({
                status: e.status,
                errorMsg: e.responseJSON.message,
            });
        }
    }
);

export const stopLiveView = createAsyncThunk(
    'post:ui/liveView',
    async (monitoredId: string, { rejectWithValue }) => {
        try {
            const response =
                await restPostDeviceCommand<ILiveViewCommandParams>(
                    { '@type': 'LIVE_VIEW', parameters: { state: 'stop' } },
                    monitoredId
                );
            return response;
        } catch (e: any) {
            throw rejectWithValue({
                status: e.status,
                errorMsg: e.responseJSON.message,
            });
        }
    }
);

export const fetchActiveLiveCameras = createAsyncThunk(
    'get:ui/liveView/cameras',
    async (monitoredId: string, { rejectWithValue }) => {
        try {
            const response = await restGetActiveLiveViews(monitoredId);
            return response;
        } catch (e: any) {
            throw rejectWithValue({
                status: e.status,
                errorMsg: e.responseJSON.message,
            });
        }
    }
);

const liveView = createSlice({
    name: 'liveView',
    initialState,
    reducers: {
        setStatus(state, action) {
            state.status = action.payload;
        },
        resetLiveViewState(state) {
            state.liveCameras = null;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchActiveLiveCameras.pending, (state) => {
            state.status = 'loading';
        });
        builder.addCase(fetchActiveLiveCameras.rejected, (state) => {
            state.status = 'error';
        });
        builder.addCase(fetchActiveLiveCameras.fulfilled, (state, action) => {
            state.status = 'done';
            state.liveCameras = action.payload;
        });
    },
});

const getCurrent = (state: TRootState) => state.ui.liveView as IState;

export const getLiveCameras = (state: TRootState) =>
    getCurrent(state).liveCameras;

export const getCamerasStatus = (state: TRootState) => getCurrent(state).status;

export const { resetLiveViewState, setStatus } = liveView.actions;

export default liveView.reducer;
